// src/pages/user/Following.jsx
import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaUserCircle, FaSearch } from 'react-icons/fa';
import { UserContext } from '../../context/userContext';
import userService from '../../services/userService';
import UserList from '../../components/user/UserList';
import Loader from '../../components/common/Loader';

const Following = () => {
  const { id } = useParams();
  const { currentUser } = useContext(UserContext);
  const [user, setUser] = useState(null);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load user profile and following list
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch user profile
        const userData = await userService.getUserProfile(id);
        setUser(userData);
        
        // Fetch following list
        const followingData = await userService.getFollowing(id);
        setFollowing(followingData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching following:', err);
        setError('Failed to load following users');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  // Handle follow status changes
  const handleFollowChange = (userId, isFollowing, status) => {
    // Update local state
    setFollowing(prevFollowing => 
      prevFollowing.map(user => 
        user._id === userId 
          ? { ...user, followInfo: { isFollowing, status } } 
          : user
      )
    );
  };

  // Filter following list based on search term
  const filteredFollowing = searchTerm
    ? following.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : following;

  return (
    <section className="py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-8 flex items-center">
          <Link 
            to={`/profile/${id}`} 
            className="mr-4 text-gray-600 hover:text-primary transition-colors"
          >
            <FaArrowLeft size={20} />
          </Link>
          
          <h1 className="text-2xl font-bold text-gray-900">
            {user ? `${user.name} is Following` : 'Following'}
          </h1>
        </div>
        
        {following.length > 0 && (
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search following..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="min-h-[200px] flex items-center justify-center">
            <Loader />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        ) : filteredFollowing.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            {searchTerm ? (
              <>
                <h3 className="text-xl font-medium text-gray-800 mb-2">No results found</h3>
                <p className="text-gray-600">
                  No followed users match your search for "{searchTerm}"
                </p>
              </>
            ) : (
              <>
                <h3 className="text-xl font-medium text-gray-800 mb-2">Not following anyone yet</h3>
                {currentUser && currentUser.id === id && (
                  <>
                    <p className="text-gray-600 mb-6">
                      When you follow people, they'll show up here.
                    </p>
                    <Link 
                      to="/authors" 
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
                    >
                      <FaSearch className="mr-2" />
                      Find Users to Follow
                    </Link>
                  </>
                )}
              </>
            )}
          </div>
        ) : (
          // If UserList component is already tailwind-styled, use it directly
          // Otherwise, implement a custom following list
          <>
            {UserList ? (
              <UserList 
                users={filteredFollowing} 
                onFollowChange={handleFollowChange}
                emptyMessage="Not following anyone yet"
              />
            ) : (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {filteredFollowing.map(followedUser => (
                    <li key={followedUser._id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                            {followedUser.avatar ? (
                              <img 
                                src={`${process.env.REACT_APP_ASSETS_URL}/uploads/profile/${followedUser.avatar}`} 
                                alt={followedUser.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center bg-gray-200">
                                <FaUserCircle className="text-gray-400" size={24} />
                              </div>
                            )}
                          </div>
                          
                          <div className="ml-4">
                            <Link 
                              to={`/profile/${followedUser._id}`}
                              className="font-medium text-gray-900 hover:text-primary transition-colors"
                            >
                              {followedUser.name}
                            </Link>
                            {followedUser.bio && (
                              <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                                {followedUser.bio}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {currentUser && currentUser.id !== followedUser._id && (
                          <button
                            onClick={() => handleFollowChange(
                              followedUser._id, 
                              !followedUser.followInfo?.isFollowing,
                              followedUser.followInfo?.isFollowing ? 'unfollowed' : 'followed'
                            )}
                            className={`px-4 py-1 text-sm font-medium rounded-full ${
                              followedUser.followInfo?.isFollowing
                                ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                : 'bg-primary text-white hover:bg-opacity-90'
                            } transition-colors`}
                          >
                            {followedUser.followInfo?.isFollowing ? 'Following' : 'Follow'}
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Following;